/**
 * Currency Context
 * Provides tenant's currency setting throughout the app
 */

import { createContext, useContext, useState, useEffect } from 'react';
import { adminAPI } from '../services/api';

const CurrencyContext = createContext();

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
}

// Currency symbol mapping
const CURRENCY_SYMBOLS = {
  EUR: '€',
  USD: '$',
  GBP: '£',
  CHF: 'Fr'
};

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState('EUR'); // Default to EUR
  const [symbol, setSymbol] = useState('€');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCurrency();
  }, []);

  const fetchCurrency = async () => {
    try {
      // Only fetch if user is authenticated (has access token)
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        // No token, skip API call and use default EUR
        setLoading(false);
        return;
      }

      // Fetch first location to get currency setting
      const response = await adminAPI.locations.getAll();
      if (response.data?.locations && response.data.locations.length > 0) {
        const currencyCode = response.data.locations[0].currency || 'EUR';
        setCurrency(currencyCode);
        setSymbol(CURRENCY_SYMBOLS[currencyCode] || '€');
      }
    } catch (err) {
      console.error('Failed to fetch currency:', err);
      // Keep EUR default - don't throw or redirect
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '';

    // Format number with 2 decimals
    const formatted = parseFloat(price).toFixed(2);

    // Return with symbol
    return `${symbol}${formatted}`;
  };

  const value = {
    currency,
    symbol,
    loading,
    formatPrice,
    setCurrency: (newCurrency) => {
      setCurrency(newCurrency);
      setSymbol(CURRENCY_SYMBOLS[newCurrency] || '€');
    }
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}
